import { createContext, useContext, useState, useCallback } from 'react';

const LanguageContext = createContext();

const translations = {
  nav: {
    experience: { fr: 'La résidence', en: 'The Residency' },
    house: { fr: 'La Maison', en: 'The House' },
    studio: { fr: 'Le Studio', en: 'The Studio' },
    artists: { fr: 'Artistes', en: 'Artists' },
    booking: { fr: 'Réserver', en: 'Book Now' },
  },
  hero: {
    subtitle: {
      fr: 'Champagne, France',
      en: 'Champagne, France',
    },
    title: {
      fr: 'Red House\nResidency',
      en: 'Red House\nResidency',
    },
    desc: {
      fr: 'Résidence artistique avec studio d\'enregistrement professionnel et hébergement sur place. À 1h40 de Paris.',
      en: 'Artist residency with professional recording studio and on-site accommodation. 1h40 from Paris.',
    },
    cta: { fr: 'Demander un devis', en: 'Request a Quote' },
    discover: { fr: 'Découvrir', en: 'Discover' },
  },
  experience: {
    label: { fr: 'La résidence', en: 'The Residency' },
    title: {
      fr: 'Une résidence artistique avec studio d\'enregistrement.',
      en: 'An artist residency with recording studio.',
    },
    desc: {
      fr: 'Red House est un lieu de vie et de travail. Un studio professionnel et une maison d\'hébergement sur le même site. Les artistes viennent ici pour travailler sans interruption, plusieurs jours ou semaines.',
      en: 'Red House is a place to live and work. A professional studio and an accommodation house on the same site. Artists come here to work without interruption, for days or weeks at a time.',
    },
    features: [
      {
        title: { fr: 'Studio + hébergement', en: 'Studio + Accommodation' },
        desc: {
          fr: 'Un studio d\'enregistrement professionnel et une grande maison familiale sur le même site.',
          en: 'A professional recording studio and a large family house on the same site.',
        },
      },
      {
        title: { fr: 'Séjours de 2 jours minimum', en: 'Minimum 2-Day Stays' },
        desc: {
          fr: 'Conçu pour les projets qui demandent du temps. La majorité des résidences durent entre 5 et 14 jours.',
          en: 'Designed for projects that need time. Most residencies last between 5 and 14 days.',
        },
      },
      {
        title: { fr: 'Cadre isolé', en: 'Secluded Setting' },
        desc: {
          fr: 'Un site calme en campagne champenoise. Pas de voisins directs, pas de distractions.',
          en: 'A quiet site in the Champagne countryside. No immediate neighbours, no distractions.',
        },
      },
      {
        title: { fr: 'Équipement professionnel', en: 'Professional Equipment' },
        desc: {
          fr: 'Console Neve VR, périphériques vintage, microphones de référence. Acoustique conçue par des ingénieurs spécialisés.',
          en: 'Neve VR console, vintage outboard, reference microphones. Acoustics designed by specialist engineers.',
        },
      },
    ],
  },
  house: {
    label: { fr: 'La Maison', en: 'The House' },
    title: { fr: 'L\'hébergement sur place', en: 'On-Site Accommodation' },
    desc: {
      fr: 'Une grande maison en pierre à proximité immédiate du studio. Chambres, cuisine équipée, piscine extérieure, bain nordique et cheminées.',
      en: 'A large stone house next to the studio. Bedrooms, equipped kitchen, outdoor pool, Nordic bath, and fireplaces.',
    },
    items: [
      { title: { fr: 'RedHouse', en: 'RedHouse' }, desc: { fr: 'Jardin de 1800m² avec maison familiale en pierre', en: '1800m² garden with stone family house' } },
      { title: { fr: 'Salons & cheminées', en: 'Living Rooms' }, desc: { fr: 'Deux salons avec cheminées et piano', en: 'Two living rooms with fireplaces and piano' } },
      { title: { fr: 'Cuisine & salle à manger', en: 'Kitchen & Dining' }, desc: { fr: 'Grande cuisine équipée pour les repas d\'équipe', en: 'Large equipped kitchen for team meals' } },
      { title: { fr: 'Chambres', en: 'Bedrooms' }, desc: { fr: '6 chambres doubles — jusqu\'à 15 personnes', en: '6 double bedrooms — up to 15 guests' } },
      { title: { fr: 'Bain nordique', en: 'Nordic Bath' }, desc: { fr: 'Bain chaud au feu de bois', en: 'Wood-fired hot tub' } },
      { title: { fr: 'Piscine', en: 'Pool' }, desc: { fr: 'Piscine extérieure saisonnière dans le jardin', en: 'Seasonal outdoor pool in the garden' } },
    ],
  },
  artists: {
    label: { fr: 'Ils sont passés par ici', en: 'They Recorded Here' },
    title: { fr: 'Ces artistes nous font confiance', en: 'Artists Who Trust Us' },
    desc: {
      fr: 'Labels, managements et artistes indépendants font confiance à Red House pour leurs projets d\'enregistrement et de création.',
      en: 'Labels, management teams, and independent artists trust Red House for their recording and creative projects.',
    },
    names: {
      fr: 'Juliette Armanet, Suzane, Georgio, Calogero, Maëlle, Bekar, L\'Impératrice, Clara Ysé, Disiz La Peste, BB Jacques, Johnny Jane, Yoa, Liv Del Estal, Zed Yun Pavarotti, Oracle Sisters, Kids Return.',
      en: 'Juliette Armanet, Suzane, Georgio, Calogero, Maëlle, Bekar, L\'Impératrice, Clara Ysé, Disiz La Peste, BB Jacques, Johnny Jane, Yoa, Liv Del Estal, Zed Yun Pavarotti, Oracle Sisters, Kids Return.',
    },
    viewAll: { fr: 'Voir tous les artistes', en: 'View All Artists' },
    showLess: { fr: 'Réduire', en: 'Show Less' },
    tierIndie: { fr: 'Indie & critique', en: 'Indie & Critical' },
    tierEmerging: { fr: 'Émergents', en: 'Emerging' },
  },
  pricing: {
    label: { fr: 'Devis sur mesure', en: 'Custom Quotes' },
    title: { fr: 'Chaque résidence est différente', en: 'Every Residency Is Different' },
    desc: {
      fr: 'Nous adaptons chaque proposition à votre projet — durée, taille de l\'équipe, besoins techniques et logistiques.',
      en: 'We tailor each proposal to your project — duration, team size, technical and logistical needs.',
    },
    subdesc: {
      fr: 'Décrivez votre projet et vos dates souhaitées.\nNous répondons sous 24 heures avec une proposition détaillée.',
      en: 'Describe your project and preferred dates.\nWe respond within 24 hours with a detailed proposal.',
    },
    cta: { fr: 'Envoyer une demande', en: 'Send an Inquiry' },
    note: { fr: 'Réponse sous 24h. Aucun engagement avant validation.', en: 'Response within 24 hours. No commitment until you approve.' },
    urgency: { fr: 'Disponibilité limitée', en: 'Limited Availability' },
    features: [
      { title: { fr: 'Studio complet', en: 'Full Studio' }, desc: { fr: 'Console Neve VR 24, microphones, périphériques et backline — tout inclus', en: 'Neve VR 24 console, microphones, outboard and backline — all included' } },
      { title: { fr: 'Hébergement', en: 'Accommodation' }, desc: { fr: 'Maison en pierre pour 6+ personnes avec piscine, bain nordique, cheminées', en: 'Stone house for 6+ guests with pool, Nordic bath, fireplaces' } },
      { title: { fr: 'Services optionnels', en: 'Optional Services' }, desc: { fr: 'Ingénieur son, restauration, accord piano', en: 'Sound engineer, catering, piano tuning' } },
    ],
  },
  team: {
    label: { fr: 'L\'équipe', en: 'The Team' },
    title: { fr: 'L\'équipe derrière Red House Residency', en: 'The Team Behind Red House Residency' },
    desc: {
      fr: 'Une équipe à taille humaine qui accompagne vos projets de A à Z.',
      en: 'A close-knit team that supports your projects from start to finish.',
    },
  },
  summerChance: {
    label: { fr: 'Summer Chance', en: 'Summer Chance' },
    title: { fr: 'Summer Chance', en: 'Summer Chance' },
    desc: {
      fr: 'Chaque été, Red House accueille une sélection d\'artistes émergents pour des résidences de création. Un programme récurrent, sur candidature.',
      en: 'Every summer, Red House hosts a selection of emerging artists for creative residencies. A recurring program, by application.',
    },
    subdesc: {
      fr: 'Candidatures ouvertes chaque printemps. Sélection sur dossier.',
      en: 'Applications open each spring. Selection based on portfolio.',
    },
    cta: { fr: 'En savoir plus', en: 'Learn More' },
  },
  howItWorks: {
    label: { fr: 'Comment réserver', en: 'How to Book' },
    title: { fr: 'Trois étapes', en: 'Three Steps' },
    cta: { fr: 'Commencer', en: 'Get Started' },
    note: { fr: 'Sans engagement — réponse sous 24h', en: 'No commitment — response within 24 hours' },
  },
  location: {
    label: { fr: 'Accès', en: 'Getting Here' },
    title: { fr: '1h40 de Paris', en: '1h40 from Paris' },
    desc: {
      fr: 'Red House est situé à La Forestière, en Champagne-Ardenne. Accessible depuis Paris par la route ou le train.',
      en: 'Red House is located in La Forestière, in the Champagne-Ardenne region. Accessible from Paris by road or train.',
    },
    paris: { fr: '1h40 de Paris', en: '1h40 from Paris' },
    parisDesc: { fr: 'Aéroports CDG & Orly, Gare de l\'Est', en: 'CDG & Orly airports, Gare de l\'Est' },
    troyes: { fr: '1h de Troyes', en: '1h from Troyes' },
    troyesDesc: { fr: 'Connexion TGV', en: 'TGV connection' },
    reims: { fr: '1h30 de Reims', en: '1h30 from Reims' },
    reimsDesc: { fr: 'Capitale du Champagne', en: 'Champagne capital' },
  },
  faq: {
    title: { fr: 'Questions fréquentes', en: 'Frequently Asked Questions' },
    desc: { fr: 'Les informations essentielles avant de réserver.', en: 'Essential information before you book.' },
    items: [
      { q: { fr: 'Où se trouve Red House ?', en: 'Where is Red House located?' }, a: { fr: 'Au 5 rue Casimir Boirot, 51120 La Forestière, en Champagne-Ardenne. À environ 1h40 de Paris par la route. Accessible depuis les aéroports CDG et Orly.', en: 'At 5 rue Casimir Boirot, 51120 La Forestière, in Champagne-Ardenne. About 1h40 from Paris by road. Accessible from CDG and Orly airports.' } },
      { q: { fr: 'Quelle est la durée minimum ?', en: 'What is the minimum stay?' }, a: { fr: '2 jours minimum. La plupart des résidences durent entre 5 et 14 jours.', en: '2 days minimum. Most residencies last between 5 and 14 days.' } },
      { q: { fr: 'Quels types de projets ?', en: 'What types of projects?' }, a: { fr: 'Enregistrement d\'albums, production d\'EP, sessions d\'écriture, mixage, et tout projet créatif long format.', en: 'Album recordings, EP production, writing sessions, mixing, and any long-format creative project.' } },
      { q: { fr: 'Un ingénieur du son est-il disponible ?', en: 'Is a sound engineer available?' }, a: { fr: 'Oui, sur demande. Ce service est optionnel et inclus dans votre proposition.', en: 'Yes, on request. This service is optional and included in your proposal.' } },
      { q: { fr: 'Transport depuis Paris ?', en: 'Transport from Paris?' }, a: { fr: 'Nous pouvons organiser des navettes depuis Paris. Détails inclus dans la proposition.', en: 'We can arrange transfers from Paris. Details included in the proposal.' } },
    ],
  },
  inquiry: {
    label: { fr: 'Commencez votre résidence', en: 'Begin Your Residency' },
    headerTitle: { fr: 'Parlez-nous de votre projet', en: 'Tell Us About Your Project' },
    headerDesc: {
      fr: 'Décrivez votre projet ci-dessous. Notre équipe vous répondra sous 24 heures avec une proposition sur mesure adaptée à vos besoins.',
      en: 'Describe your project below. Our team will respond within 24 hours with a tailored proposal designed for your needs.',
    },
    step1: { fr: 'Identité', en: 'Identity' },
    step2: { fr: 'Projet', en: 'Project Details' },
    step3: { fr: 'Logistique & Dates', en: 'Logistics & Dates' },
    step4: { fr: 'Récapitulatif', en: 'Review Your Inquiry' },
    firstName: { fr: 'Prénom *', en: 'First Name *' },
    lastName: { fr: 'Nom *', en: 'Last Name *' },
    entityLabel: { fr: 'Entité / Label', en: 'Entity / Label' },
    email: { fr: 'Email *', en: 'Email *' },
    phone: { fr: 'Téléphone *', en: 'Phone *' },
    projectType: { fr: 'Type de projet *', en: 'Type of Project *' },
    projectTypes: {
      album: { fr: 'Album', en: 'Album' },
      writing_residency: { fr: 'Résidence d\'écriture', en: 'Writing Residency' },
      mixing: { fr: 'Mixage', en: 'Mixing' },
      production: { fr: 'Production', en: 'Production' },
      other: { fr: 'Autre', en: 'Other' },
    },
    numPeople: { fr: 'Nombre de personnes *', en: 'Number of People *' },
    numPeoplePlaceholder: { fr: 'ex: 4', en: 'e.g. 4' },
    estimatedDuration: { fr: 'Durée estimée *', en: 'Estimated Duration *' },
    durationPlaceholder: { fr: 'ex: 1 semaine, 10 jours...', en: 'e.g. 1 week, 10 days...' },
    needEngineer: { fr: 'Avez-vous besoin d\'un ingénieur du son ?', en: 'Do you need a sound engineer?' },
    engineerOptions: {
      yes_full_time: { fr: 'Oui', en: 'Yes' },
      no: { fr: 'Non', en: 'No' },
      not_sure: { fr: 'Pas sûr', en: 'Not Sure' },
    },
    instruments: { fr: 'Instruments (sélectionnez)', en: 'Instruments (select all that apply)' },
    pianoTuning: { fr: 'Accordage piano nécessaire', en: 'Piano tuning required' },
    flexibleDates: { fr: 'Dates flexibles', en: 'Flexible with dates' },
    accommodation: { fr: 'Hébergement *', en: 'Accommodation *' },
    fullHouse: { fr: 'Maison complète', en: 'Full House' },
    fullHouseDesc: { fr: 'Résidence complète avec hébergement', en: 'Complete residency experience' },
    studioOnly: { fr: 'Studio uniquement', en: 'Studio Only' },
    studioOnlyDesc: { fr: 'Accès studio professionnel', en: 'Professional studio access' },
    selectDates: { fr: 'Sélectionnez vos dates souhaitées', en: 'Select Your Preferred Dates' },
    daysSelected: { fr: 'jours sélectionnés', en: 'days selected' },
    pendingInquiry: { fr: 'demande en cours', en: 'pending inquiry' },
    datesDisclaimer: {
      fr: 'La soumission de vos dates ne confirme pas la réservation. Notre équipe examinera et enverra une proposition sur mesure.',
      en: 'Submitting your dates does not confirm booking. Our team will review and send a custom proposal.',
    },
    catering: { fr: 'Intéressé par un service de restauration', en: 'Interested in catering services' },
    specialRequests: { fr: 'Demandes techniques particulières', en: 'Special Technical Requests' },
    specialRequestsPlaceholder: { fr: 'Besoins spécifiques, configuration technique, etc.', en: 'Specific equipment needs, technical setup, etc.' },
    additionalMessage: { fr: 'Message complémentaire', en: 'Additional Message' },
    messagePlaceholder: { fr: 'Parlez-nous de votre projet...', en: 'Tell us more about your project...' },
    previous: { fr: 'Étape précédente', en: 'Previous step' },
    nextStep: { fr: 'Étape suivante', en: 'Next Step' },
    backToSite: { fr: '← Retour', en: '← Back' },
    review: { fr: 'Vérifier', en: 'Review' },
    reviewContact: { fr: 'Contact', en: 'Contact' },
    reviewProject: { fr: 'Projet', en: 'Project' },
    reviewDates: { fr: 'Dates', en: 'Dates' },
    reviewLogistics: { fr: 'Logistique', en: 'Logistics' },
    reviewFlexible: { fr: 'Flexible', en: 'Flexible' },
    reviewCatering: { fr: 'Restauration souhaitée', en: 'Catering interest' },
    reviewEngineer: { fr: 'Ingénieur :', en: 'Engineer:' },
    reviewDisclaimer: {
      fr: 'En soumettant cette demande, vous acceptez que notre équipe examine votre projet et vous réponde sous 24 heures avec une proposition sur mesure. Ceci n\'est pas une réservation confirmée.',
      en: 'By submitting this inquiry, you agree that our team will review your project details and respond within 24 hours with a tailored proposal. This is not a confirmed booking.',
    },
    submit: { fr: 'Envoyer ma demande', en: 'Request My Tailored Proposal' },
    submitting: { fr: 'Envoi en cours...', en: 'Submitting...' },
    confirmTitle: { fr: 'Votre demande de résidence\na bien été reçue', en: 'Your Residency Inquiry\nHas Been Received' },
    confirmDesc: { fr: 'Merci. Nous reviendrons vers vous rapidement avec une proposition sur mesure.', en: 'Thank you. We\'ll be in touch shortly with your tailored proposal.' },
    whatHappensNext: { fr: 'Prochaines étapes', en: 'What Happens Next' },
    confirmStep1Title: { fr: 'Analyse (sous 2 heures)', en: 'Review (within 2 hours)' },
    confirmStep1Desc: { fr: 'Notre équipe examinera attentivement votre projet et la disponibilité.', en: 'Our team will carefully review your project requirements and availability.' },
    confirmStep2Title: { fr: 'Proposition sur mesure (sous 24h)', en: 'Custom Proposal (within 24 hours)' },
    confirmStep2Desc: { fr: 'Nous préparerons un package résidence personnalisé avec tarifs et spécifications techniques.', en: 'We\'ll design a tailored residency package with detailed pricing and technical specifications.' },
    confirmStep3Title: { fr: 'Confirmation', en: 'Confirmation' },
    confirmStep3Desc: { fr: 'Une fois la proposition validée, nous vous enverrons la confirmation de réservation et tous les détails.', en: 'Once you approve the proposal, we\'ll send booking confirmation and full details.' },
    emailSent: { fr: 'Nous avons envoyé une confirmation à', en: 'We\'ve sent a confirmation to' },
    noEmail: { fr: 'Pour toute question, n\'hésitez pas à nous contacter directement.', en: 'For any questions, feel free to reach out directly.' },
    returnHome: { fr: 'Retour à l\'accueil', en: 'Return Home' },
  },
  footer: {
    desc: { fr: 'Résidence d\'enregistrement en Champagne.', en: 'Recording residency in Champagne, France.' },
    contact: { fr: 'Contact', en: 'Contact' },
    address: { fr: 'Adresse', en: 'Address' },
    legal: { fr: 'Mentions légales', en: 'Legal notice' },
    privacy: { fr: 'Politique de confidentialité', en: 'Privacy policy' },
    back: { fr: '← Retour', en: '← Back' },
  },
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState('fr');
  const toggle = useCallback(() => setLang(l => l === 'fr' ? 'en' : 'fr'), []);
  const t = useCallback((path) => {
    const keys = path.split('.');
    let val = translations;
    for (const k of keys) val = val?.[k];
    if (val && typeof val === 'object' && (val.fr || val.en)) return val[lang] || val.fr;
    return val || path;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, toggle, t, translations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
